import { apiClient } from "../../../shared/api/client";
import { generateIdempotencyKey } from "../../../shared/api/idempotency";
import type { Wallet, WalletTransaction } from "../../../entities/wallet";

// ─── Solde courant ────────────────────────────────────────────────────────────

export async function getMyWallet(): Promise<Wallet> {
  const { data } = await apiClient.get<Wallet>("/wallet");
  return data;
}

// ─── Historique des transactions ──────────────────────────────────────────────
// Le backend retourne les 100 dernières transactions, sans filtre côté API
// → on filtre côté client (cf. filterTransactions).

export async function getWalletTransactions(): Promise<WalletTransaction[]> {
  const { data } = await apiClient.get<WalletTransaction[]>("/wallet/transactions");
  return data;
}

export type TransactionFilter = "all" | "in" | "out" | "orders";

export function filterTransactions(
  txs: WalletTransaction[],
  filter: TransactionFilter,
): WalletTransaction[] {
  if (filter === "all") return txs;
  // SURPLUS_RELEASE compte parmi les ENTRÉES : c'est de l'argent rendu au client
  // en fin de course. Omis, il n'apparaissait dans AUCUN filtre.
  if (filter === "in")
    return txs.filter(
      (t) => t.type === "TOPUP" || t.type === "RELEASE" || t.type === "SURPLUS_RELEASE",
    );
  if (filter === "out")   return txs.filter((t) => t.type === "HOLD"  || t.type === "CAPTURE");
  // orders : tout ce qui touche à une commande
  return txs.filter((t) => t.reference_type === "ORDER");
}

// ─── Stats client-side (le backend n'expose pas d'agrégat dédié) ─────────────

export type WalletMonthlyStats = {
  inThisMonth:  number; // XOF entier
  outThisMonth: number;
};

export function computeMonthlyStats(txs: WalletTransaction[]): WalletMonthlyStats {
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const monthStartMs = monthStart.getTime();

  let inThisMonth  = 0;
  let outThisMonth = 0;

  for (const t of txs) {
    if (!t.created_at) continue;
    if (new Date(t.created_at).getTime() < monthStartMs) continue;
    const amount = t.amount ?? 0;
    // SURPLUS_RELEASE = crédit. Omis, un remboursement de surplus n'était
    // comptabilisé ni en entrées ni en sorties : il disparaissait du bilan.
    if (t.type === "TOPUP" || t.type === "RELEASE" || t.type === "SURPLUS_RELEASE") {
      inThisMonth += amount;
    }
    if (t.type === "HOLD"  || t.type === "CAPTURE") outThisMonth += amount;
  }

  return { inThisMonth, outThisMonth };
}

// ─── Recharge wallet (idempotent) ─────────────────────────────────────────────

export type TopupPayload = {
  amount:    number;        // XOF entier
  currency?: string;        // défaut XOF
};

// `idempotencyKey` est OBLIGATOIRE au contrat depuis openapi v2.1.0 (422 si
// absente) et doit rester STABLE entre les retentatives d'une même recharge —
// sinon un re-tap après timeout créditerait deux fois. Même patron que
// createTrip / initiatePayment : c'est l'appelant qui détient la clé.
//
// NOTE : dans le flow recharge nominal, l'app n'appelle PAS /wallet/topup —
// c'est `POST /payments/initiate` qui démarre, et le webhook PSP crédite le
// wallet côté serveur. Ce service reste pour les usages hors flow PSP.
export async function topupWallet(
  payload:         TopupPayload,
  idempotencyKey?: string,
): Promise<WalletTransaction> {
  const { data } = await apiClient.post<WalletTransaction>(
    "/wallet/topup",
    payload,
    { headers: { "Idempotency-Key": idempotencyKey ?? generateIdempotencyKey() } },
  );
  return data;
}
